import { AccountSetupRequest } from "@/types/payments/AccountSetupRequest";
import { AccountSetupResponse } from "@/types/payments/AccountSetupResponse";
import { CheckoutSessionRequest } from "@/types/payments/CheckoutSessionRequest";
import { CheckoutSessionResponse } from "@/types/payments/CheckoutSessionResponse";
import { FreeTrialRequest } from "@/types/payments/FreeTrialRequest";
import { FreeTrialResponse } from "@/types/payments/FreeTrialResponse";
import { SubscriptionInfo } from "@/types/payments/SubscriptionInfo";

const getSubscriptionInfo = async (paymentProvider: string, sessionId: string): Promise<SubscriptionInfo | null> => {
	const url = `${(window as any).env.VITE_SUBSCRIPTIONS_URL}/api`;
	try {
		const response = await fetch(`${url}/v1/accountsetup/${paymentProvider}/session/${sessionId}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"X-API-Key": (window as any).env.VITE_SUBSCRIPTIONS_API_KEY
			}
		});

		if (response != null && response.ok) {
			return (await response.json()) as SubscriptionInfo;
		}

		console.warn("[payments.getSubscriptionInfo] Account setup details request returned an unsuccessful response.", {
			extra: {
				paymentProvider,
				sessionIdPresent: Boolean(sessionId),
				status: response?.status
			},
			tags: {
				file: "payment",
				method: "getSubscriptionInfo"
			}
		});
	} catch (err: any) {
		console.error(err, {
			extra: {
				paymentProvider: paymentProvider,
				sessionId: sessionId
			},
			tags: {
				file: "payment",
				method: "getSubscriptionInfo"
			}
		});
	}

	return null;
};

const setupAccount = async (request: AccountSetupRequest): Promise<AccountSetupResponse> => {
	const url = `${(window as any).env.VITE_SUBSCRIPTIONS_URL}/api`;

	try {
		const response = await fetch(`${url}/v1/accountsetup`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-API-Key": (window as any).env.VITE_SUBSCRIPTIONS_API_KEY
			},
			body: JSON.stringify(request)
		});

		if (response != null && response.ok) {
			return (await response.json()) as AccountSetupResponse;
		}

		console.warn("[payments.setupAccount] Account setup request returned an unsuccessful response.", {
			extra: {
				subscriptionTransactionIdPresent: Boolean(request.subscriptionTransactionId),
				status: response?.status
			},
			tags: {
				file: "payment",
				method: "setupAccount"
			}
		});
	} catch (err) {
		console.error(err, {
			extra: {
				subscriptionTransactionId: request.subscriptionTransactionId
			},
			tags: {
				file: "payment",
				method: "setupAccount"
			}
		});
	}

	return {
		error: true,
		message: "Failed to setup account. API request failed",
		subscriptionTransactionId: request.subscriptionTransactionId
	};
};

const createFreeTrial = async (request: FreeTrialRequest): Promise<FreeTrialResponse> => {
	const url = `${(window as any).env.VITE_SUBSCRIPTIONS_URL}/api/v1/signup/freetrial`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json"
		},
		body: JSON.stringify(request)
	});

	if (!response.ok) {
		let message = "Something went wrong, please try again.";
		try {
			const body = await response.json();
			if (body?.message) {
				message = body.message;
			}
		} catch {
			// fall through to generic message
		}
		throw new Error(message);
	}

	return (await response.json()) as FreeTrialResponse;
};

const createCheckoutSession = async (request: CheckoutSessionRequest): Promise<CheckoutSessionResponse> => {
	const url = `${(window as any).env.VITE_SUBSCRIPTIONS_URL}/api/v1/checkout/session`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"X-API-Key": (window as any).env.VITE_SUBSCRIPTIONS_API_KEY
		},
		body: JSON.stringify(request)
	});

	if (!response.ok) {
		let message = "Something went wrong. Please try again.";
		let code: string | undefined;

		try {
			const body = await response.json();

			if (body?.message) {
				message = body.message;
			}

			if (body?.code) {
				code = body.code;
			}
		} catch {
			// fall through to generic message
		}

		const error: Error & { code?: string } = new Error(message);
		error.code = code;
		throw error;
	}

	return (await response.json()) as CheckoutSessionResponse;
};

export { getSubscriptionInfo, setupAccount, createFreeTrial, createCheckoutSession };
